/**
 * Keyword parsing, message matching, and sender-brand extraction.
 *
 * Lives in `shared` because both sides need it: the worker matches incoming
 * mail against it, and the web app previews what a string will match before
 * the user saves it. One implementation means the preview cannot disagree with
 * the real thing.
 */
/** Longest a single keyword may be, to stop a pasted essay becoming one term. */
const MAX_KEYWORD_LENGTH = 120;
/** Generous — these lists are routinely dozens of sender domains. */
const MAX_KEYWORDS = 200;
/** `OR` used as a separator, not as something to search for. */
const IS_OR = /^or$/i;
/**
 * Splits what the user typed into keywords.
 *
 * Accepts both styles people actually paste:
 *
 *     amazon, flipkart, "black friday"
 *     "usemailora" OR "quickmortgagetoday" OR "info@biz2credit.com"
 *
 * Separators are commas, semicolons, pipes, newlines, and the word `OR`.
 * Anything inside double quotes is taken literally, so an address such as
 * `contact@e.allied-agency.com` survives intact.
 *
 * Written as a small tokenizer rather than chained split/replace because real
 * pasted lists are malformed. `"maison7"OR " OR "info@x.com"` — a missing
 * space plus a stray quoted separator — previously produced a keyword of
 * literally `or`, which matches almost every email and so silently disabled
 * the entire filter.
 */
export function parseKeywords(raw) {
    const terms = [];
    let buffer = '';
    /** Unquoted runs split on whitespace; a bare `OR` between them is dropped. */
    const flushBuffer = () => {
        const value = buffer.trim();
        buffer = '';
        if (!value)
            return;
        for (const word of value.split(/\s+/)) {
            if (word && !IS_OR.test(word))
                terms.push(word);
        }
    };
    for (let i = 0; i < raw.length; i += 1) {
        const char = raw[i];
        if (char === '"') {
            flushBuffer();
            const close = raw.indexOf('"', i + 1);
            // An unterminated quote takes the rest of the input rather than
            // discarding it — a truncated paste should still mostly work.
            const phrase = (close === -1 ? raw.slice(i + 1) : raw.slice(i + 1, close)).trim();
            if (phrase && !IS_OR.test(phrase))
                terms.push(phrase);
            i = close === -1 ? raw.length : close;
            continue;
        }
        if (char === ',' || char === ';' || char === '|' || char === '\n' || char === '\r') {
            flushBuffer();
            continue;
        }
        buffer += char;
    }
    flushBuffer();
    const normalised = terms
        .map((term) => term.toLowerCase().replace(/\s+/g, ' ').trim())
        .filter((term) => term.length > 0 && term.length <= MAX_KEYWORD_LENGTH);
    return [...new Set(normalised)].slice(0, MAX_KEYWORDS);
}
/**
 * Reads back a list that was persisted as JSON text.
 *
 * Keyword sets and matched-batch ids both live in string columns, because
 * SQLite has no array type. Unreadable content yields an empty list rather than
 * throwing: one corrupt row must not blank a dashboard or stall ingestion.
 * Callers that cannot treat "empty" and "unreadable" alike check the length.
 */
export function parseStoredList(raw) {
    if (!raw)
        return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((value) => typeof value === 'string') : [];
    }
    catch {
        return [];
    }
}
/**
 * Matches a message against every active batch at once.
 *
 * A message satisfying two batches is recorded against both, so per-batch
 * figures stay correct when a user runs several keyword sets in parallel.
 *
 * With no batches at all nothing matches — see below.
 */
export function matchesAnyBatch(target, batches) {
    /**
     * No active batch means nothing matches — the system is off, not wide open.
     *
     * This used to return `matched: true`, so an account with no keywords opened
     * every link in every message that arrived. That is the most expensive
     * possible behaviour, applied at exactly the moment the user has expressed no
     * intent at all: a mailbox connected before any keywords were saved would
     * start visiting its entire inbox. Doing nothing until asked is the only
     * defensible default.
     */
    if (batches.length === 0)
        return { matched: false, batchIds: [], keywords: [] };
    const batchIds = [];
    const keywords = new Set();
    for (const batch of batches) {
        const result = matchesFilter(target, batch.keywords);
        if (!result.matched)
            continue;
        batchIds.push(batch.id);
        for (const keyword of result.keywords)
            keywords.add(keyword);
    }
    return { matched: batchIds.length > 0, batchIds, keywords: [...keywords] };
}
/**
 * Case-insensitive substring match across the parts of a message a person
 * would actually scan: subject, sender, brand, and the opening text.
 *
 * Substring rather than whole-word on purpose — "sale" should match
 * "MEGA-SALE!!" and "flipkart" should match "flipkart.com". Whole-word
 * matching fails constantly on marketing subject lines, which are written to
 * be shouty rather than grammatical.
 *
 * An empty keyword list matches everything, but no caller passes one: batches
 * with no readable keywords are dropped before they reach here, and a user with
 * no batches never gets this far. See `matchesAnyBatch`.
 */
export function matchesFilter(target, keywords) {
    if (keywords.length === 0)
        return { matched: true, keywords: [] };
    const haystack = [target.subject, target.fromAddress, target.fromName, target.snippet, target.brand]
        .filter(Boolean)
        .join('  ')
        .toLowerCase();
    const hits = keywords.filter((keyword) => haystack.includes(keyword));
    return { matched: hits.length > 0, keywords: hits };
}
/**
 * Normalises a sender into a brand name for the leaderboard.
 *
 * Prefers the registrable domain over the display name, because display names
 * are inconsistent ("Amazon", "Amazon.in", "Amazon Deals") while the sending
 * domain is stable. Common mail-infrastructure subdomains are stripped so
 * "email.amazon.in" and "news.amazon.in" collapse to one brand.
 */
const INFRA_LABELS = new Set([
    'email',
    'mail',
    'mailer',
    'news',
    'newsletter',
    'notify',
    'notification',
    'notifications',
    'reply',
    'noreply',
    'no-reply',
    'send',
    'sender',
    'smtp',
    'mktg',
    'marketing',
    'info',
    'updates',
    'alerts',
    'e',
    'em',
    't',
]);
/** Multi-part public suffixes we must not mistake for the brand. */
const COMPOUND_TLDS = new Set([
    'co.uk', 'co.in', 'com.au', 'co.nz', 'co.jp', 'co.kr', 'com.br', 'com.mx',
    'co.za', 'com.sg', 'com.tr', 'com.cn', 'org.uk', 'net.au', 'ac.uk', 'gov.uk',
]);
export function extractBrand(fromAddress, fromName) {
    const domain = fromAddress?.split('@')[1]?.trim().toLowerCase().replace(/\.$/, '');
    if (domain && domain.includes('.')) {
        const labels = domain.split('.').filter(Boolean);
        // Work out how many trailing labels belong to the public suffix.
        const lastTwo = labels.slice(-2).join('.');
        const suffixLength = COMPOUND_TLDS.has(lastTwo) ? 2 : 1;
        let candidateIndex = labels.length - suffixLength - 1;
        // Walk left past mail-infrastructure labels: news.email.amazon.in → amazon
        while (candidateIndex > 0 && INFRA_LABELS.has(labels[candidateIndex])) {
            candidateIndex -= 1;
        }
        const brand = labels[candidateIndex];
        if (brand && brand.length > 1 && !INFRA_LABELS.has(brand)) {
            return brand;
        }
    }
    // Fall back to the display name when the domain tells us nothing useful.
    const name = fromName?.trim().toLowerCase().replace(/\s+/g, ' ');
    if (name && name.length > 1 && name.length <= 40) {
        return name.replace(/\s*(mail|team|support|noreply|no-reply)\s*$/i, '').trim() || null;
    }
    return null;
}
//# sourceMappingURL=matching.js.map